import {
  Alignment,
  Classes,
  Collapse,
  Label,
  PanelProps,
  Position,
  Radio,
  RadioGroup,
  Switch,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { ModelSelector } from 'src/components/ModelSelector';
import {
  TEST_IDS,
  getPublishProjectDrawerCreationTypeTestId,
} from 'src/constants/testIds';
import { useValidateSensitivePropertyGroupPublishName } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useSensitivePropertyGroups';
import {
  useDefaultModelInfo,
  useDefaultSPGInfo,
  useFindModel,
  useModelNamePlaceholder,
  useModelNamedGraphPlaceholder,
  usePublish,
  useValidateModelName,
  useValidateNamedGraphIri,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  MappingConfigPanel,
  MappingConfigPanelProps,
} from 'src/containers/PublishProjectDrawer/MappingConfigPanel';
import { ModelConfigSPG } from 'src/containers/PublishProjectDrawer/ModelConfigSPG';
import {
  OpenInExplorerPanel,
  OpenInExplorerPanelProps,
} from 'src/containers/PublishProjectDrawer/OpenInExplorerPanel';
import { PublishProjectDrawerFooter } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerFooter';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  ConnectionInfo,
  CreationType,
  ModelInfo,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { hasValidMappingsSelector } from 'src/selectors/hasValidMappings';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { ModelSelectItem } from 'src/selectors/modelItems';
import { projectSelector } from 'src/selectors/project';
import {
  projectInvalidRulesByIdSelector,
  projectRulesByIdSelector,
} from 'src/selectors/projectRules';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type ModelConfigPanelProps = {
  connectionInfo: ConnectionInfo;
};

export const ModelConfigPanel: React.VFC<PanelProps<ModelConfigPanelProps>> = ({
  closePanel,
  connectionInfo,
  openPanel,
}) => {
  const { databaseId } = connectionInfo;
  const { projectId } = useRouterParams();
  const hasValidMappings = useReduxSelector(
    hasValidMappingsSelector(projectId)
  );
  const project = useReduxSelector(projectSelector(projectId));
  const ruleInfo = useReduxSelector(projectRulesByIdSelector(projectId));

  const didDatabaseChange = useDidValueChange(databaseId);
  const refreshedDatabase = useReduxSelector(
    isDatabaseRefreshedSelector(databaseId)
  );
  const didRefreshedDatabaseChange = useDidValueChange(refreshedDatabase);

  const getDefaultModelInfo = useDefaultModelInfo(databaseId);
  const getDefaultSPGInfo = useDefaultSPGInfo(databaseId);
  const findModel = useFindModel(databaseId);
  const validateModelName = useValidateModelName(databaseId);
  const validateNamedGraph = useValidateNamedGraphIri(databaseId);
  const modelNamePlaceholder = useModelNamePlaceholder(databaseId);
  const namedGraphIriPlaceholder = useModelNamedGraphPlaceholder(databaseId);
  const publish = usePublish();

  const initialModelInfo = getDefaultModelInfo();
  const [useAdvancedSettings, setUseAdvancedSettings] = React.useState(false);
  const [modelInfo, setModelInfo] = React.useState(initialModelInfo);

  const [isPublishingRules, setIsPublishingRules] = React.useState(
    project?.publishHistory.isPublishingRules ?? true
  );
  const initialSPGInfo = getDefaultSPGInfo();
  const [spgInfo, setSPGInfo] = React.useState(initialSPGInfo);
  const [isPublishingSPG, setIsPublishingSPG] = React.useState(
    project?.publishHistory.isPublishingSPG ?? true
  );
  const [spgError, setSpgError] = React.useState<string | null>(null);

  const initialExistingModel = findModel(initialModelInfo.modelName);
  const [existing, setExisting] = React.useState<ModelSelectItem | null>(
    initialExistingModel || null
  );

  React.useEffect(() => {
    if (
      (didDatabaseChange || didRefreshedDatabaseChange) &&
      refreshedDatabase
    ) {
      setModelInfo(initialModelInfo);
      setSPGInfo(initialSPGInfo);
      setExisting(initialExistingModel || null);
      setUseAdvancedSettings(
        Boolean(
          initialModelInfo.creationType === CreationType.NEW &&
            (validateModelName(initialModelInfo.modelName) ||
              validateNamedGraph(initialModelInfo.namedGraphIri))
        )
      );
    }
  }, [
    didDatabaseChange,
    didRefreshedDatabaseChange,
    initialExistingModel,
    initialModelInfo,
    initialSPGInfo,
    project,
    refreshedDatabase,
    validateModelName,
    validateNamedGraph,
  ]);

  const handleSetInfo = React.useCallback(
    (nextInfo: Partial<ModelInfo>) => {
      setModelInfo({ ...modelInfo, ...nextInfo });
    },
    [modelInfo, setModelInfo]
  );

  const { creationType, modelName, namedGraphIri } = modelInfo;
  const handleChangeUseAdvancedSettings = React.useCallback(
    ({ currentTarget: { checked } }: React.FormEvent<HTMLInputElement>) => {
      setUseAdvancedSettings(checked);
      if (!checked && creationType === CreationType.NEW) {
        handleSetInfo({
          modelName: modelNamePlaceholder,
          namedGraphIri: namedGraphIriPlaceholder,
        });
      }
    },
    [
      creationType,
      handleSetInfo,
      modelNamePlaceholder,
      namedGraphIriPlaceholder,
    ]
  );

  const handleSelectExisting = React.useCallback(
    (existing: ModelSelectItem | null) => {
      setExisting(existing);
      if (existing?.value) {
        handleSetInfo({
          modelName: existing.value,
          namedGraphIri: existing.namedGraph || '',
        });
      }
    },
    [handleSetInfo]
  );

  const handleChangeCreationType = React.useCallback(
    ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
      const newCreationType = value as CreationType;

      if (newCreationType === CreationType.NEW) {
        handleSetInfo({
          creationType: newCreationType,
          modelName: modelNamePlaceholder,
          namedGraphIri: namedGraphIriPlaceholder,
        });
        if (!modelNamePlaceholder) {
          setUseAdvancedSettings(true);
        }
      } else if (newCreationType === CreationType.REPLACE) {
        handleSetInfo({
          creationType: newCreationType,
          modelName: existing?.value || '',
          namedGraphIri: existing?.namedGraph || '',
        });
      } else if (newCreationType === CreationType.APPEND) {
        handleSetInfo({
          creationType: newCreationType,
          modelName: existing?.value || '',
          namedGraphIri: existing?.namedGraph || '',
        });
      }
    },
    [existing, handleSetInfo, modelNamePlaceholder, namedGraphIriPlaceholder]
  );

  const validateSPGCreateName = useValidateSensitivePropertyGroupPublishName(
    databaseId,
    spgInfo
  );

  const handleUpdateSPG = React.useCallback(
    (update: SensitivePropertyGroupsInfo) => {
      setSPGInfo(update);
      let publishError = '';
      if (isPublishingSPG) {
        Object.values(update).forEach((spg) => {
          if (spg.actionType === CreationType.NEW) {
            const error = validateSPGCreateName(spg);
            if (error) {
              publishError = error;
            }
          } else if (spg.publishName === '') {
            publishError =
              copy.components.publishProjectDrawer.modelConfiguration.sensitivePropertyGroups.replaceOrAppendUnnamedGroup(
                spg.spgName
              );
          }
        });
      }
      setSpgError(publishError);
    },
    [isPublishingSPG, setSPGInfo, setSpgError, validateSPGCreateName]
  );

  const nextButtonError = React.useMemo(() => {
    if (creationType === CreationType.NEW) {
      const modelNameError = validateModelName(modelName);
      if (modelNameError) {
        return modelNameError;
      }

      const namedGraphError = validateNamedGraph(namedGraphIri);
      if (namedGraphError) {
        return namedGraphError;
      }
    } else if (!modelName) {
      return copy.components.publishProjectDrawer.validation.modelName.required;
    }
    if (isPublishingSPG && spgError) {
      return spgError;
    }

    return '';
  }, [
    creationType,
    modelName,
    namedGraphIri,
    validateModelName,
    validateNamedGraph,
    spgError,
    isPublishingSPG,
  ]);

  const handleNext = React.useCallback(() => {
    if (nextButtonError) {
      return;
    }

    if (hasValidMappings) {
      const props: MappingConfigPanelProps = {
        connectionInfo,
        isPublishingRules,
        isPublishingSPG,
        modelInfo,
        ruleInfo,
        spgInfo,
      };
      openPanel({ renderPanel: MappingConfigPanel, props });
    } else {
      const props: OpenInExplorerPanelProps = {
        connectionInfo,
        isPublishingRules,
        modelInfo,
        ruleInfo,
      };
      openPanel({ renderPanel: OpenInExplorerPanel, props });
      publish({ ...props, isPublishingSPG, projectId, spgInfo });
    }
  }, [
    connectionInfo,
    hasValidMappings,
    isPublishingRules,
    isPublishingSPG,
    modelInfo,
    nextButtonError,
    openPanel,
    projectId,
    publish,
    ruleInfo,
    spgInfo,
  ]);
  const invalidRules = useReduxSelector(
    projectInvalidRulesByIdSelector(projectId)
  );

  const ruleWarning = React.useMemo(() => {
    if (isPublishingRules && Object.keys(invalidRules).length > 0) {
      return copy.components.publishProjectDrawer.modelConfiguration.rules
        .incompleteRulesWarning;
    }
    return '';
  }, [isPublishingRules, invalidRules]);

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.model.container}
      >
        <PublishProjectDrawerStepper active="model" />

        <Label>
          {
            copy.components.publishProjectDrawer.modelConfiguration
              .modelSelection
          }
        </Label>
        <div>
          <RadioGroup
            onChange={handleChangeCreationType}
            selectedValue={creationType}
          >
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.NEW
              )}
              label={
                copy.components.publishProjectDrawer.modelConfiguration
                  .modelName.create
              }
              value={CreationType.NEW}
            />
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.REPLACE
              )}
              labelElement={
                <>
                  {
                    copy.components.publishProjectDrawer.modelConfiguration
                      .modelName.replace
                  }

                  <div className="sd-drawer-selector-menu">
                    <ModelSelector
                      databaseId={databaseId}
                      disabled={creationType !== CreationType.REPLACE}
                      popoverPosition={Position.BOTTOM_LEFT}
                      selectedModel={existing}
                      setSelectedModel={handleSelectExisting}
                    />
                  </div>
                </>
              }
              value={CreationType.REPLACE}
            />
          </RadioGroup>
        </div>

        <div>
          <Switch
            alignIndicator={Alignment.RIGHT}
            checked={useAdvancedSettings}
            className={classNames(
              'sd-drawer-use-advanced-settings',
              'sd-label-switch'
            )}
            data-testid={TEST_IDS.publishProjectDrawer.useAdvancedSettings}
            inline
            label={
              copy.components.publishProjectDrawer.modelConfiguration
                .advancedModelSwitch
            }
            onChange={handleChangeUseAdvancedSettings}
          />
          <Collapse className="sd-drawer-margin" isOpen={useAdvancedSettings}>
            <InlineEditableLabel
              disabled={creationType === CreationType.REPLACE}
              label={
                copy.components.publishProjectDrawer.modelConfiguration
                  .modelName.label
              }
              onSetValue={(nextModelName) =>
                handleSetInfo({ modelName: nextModelName })
              }
              onValidate={
                creationType === CreationType.REPLACE
                  ? () => ''
                  : validateModelName
              }
              placeholder={modelNamePlaceholder}
              testId={TEST_IDS.publishProjectDrawer.model.modelNameInput}
              value={
                creationType === CreationType.REPLACE && !existing
                  ? copy.components.publishProjectDrawer.modelConfiguration
                      .modelName.placeholder
                  : modelName
              }
            />
            <InlineEditableLabel
              disabled={creationType === CreationType.REPLACE}
              label={
                copy.components.publishProjectDrawer.modelConfiguration
                  .namedGraph.label
              }
              onSetValue={(nextNamedGraphIri) =>
                handleSetInfo({ namedGraphIri: nextNamedGraphIri })
              }
              onValidate={
                creationType === CreationType.REPLACE
                  ? () => ''
                  : validateNamedGraph
              }
              placeholder={namedGraphIriPlaceholder}
              testId={TEST_IDS.publishProjectDrawer.model.namedGraphIriInput}
              value={
                creationType === CreationType.REPLACE && !existing
                  ? copy.components.publishProjectDrawer.modelConfiguration
                      .modelName.placeholder
                  : namedGraphIri
              }
            />
          </Collapse>
        </div>

        {Object.keys(spgInfo).length ? (
          <ModelConfigSPG
            databaseId={databaseId}
            handleUpdateSPG={handleUpdateSPG}
            isPublishingSPG={isPublishingSPG}
            sensitivePropertyGroups={spgInfo}
            setIsPublishingSPG={setIsPublishingSPG}
          />
        ) : null}

        {Object.keys(ruleInfo).length ? (
          <div className="sd-drawer-group-label">
            <Switch
              alignIndicator={Alignment.RIGHT}
              checked={isPublishingRules}
              className="sd-label-switch"
              data-testid={TEST_IDS.publishProjectDrawer.model.rules.toggle}
              inline
              label={
                copy.components.publishProjectDrawer.modelConfiguration.rules
                  .label
              }
              onChange={() => setIsPublishingRules(!isPublishingRules)}
            />
          </div>
        ) : null}
      </div>

      <PublishProjectDrawerFooter
        handleBack={closePanel}
        handleNext={handleNext}
        nextError={nextButtonError}
        nextTestId={
          hasValidMappings
            ? TEST_IDS.publishProjectDrawer.footer.nextToMappingButton
            : TEST_IDS.publishProjectDrawer.footer.publishButton
        }
        nextText={
          hasValidMappings
            ? copy.components.publishProjectDrawer.footer.next
            : copy.components.publishProjectDrawer.footer.publish
        }
        warningCallout={ruleWarning}
      />
    </>
  );
};
